import type { GridColDef } from '@mui/x-data-grid-pro'
import Button from 'ext-ui/components/Button/Button'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import Pill from 'ext-ui/components/Pill/Pill'
import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  text_colorTextPrimary,
} from 'ext-ui/style/colors'
import {
  font_family_weight_regular,
  font_size_md,
} from 'ext-ui/style/typography'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import DataGrid, {
  DATA_GRID_UNMEASURED_BORDER_HEIGHT,
  DataGridHeaderStyle,
  DataGridRowHeight,
} from 'src/components/DataGrid/DataGrid'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
// Shared with the stats roll-up and the sibling Overview tables so the tab has
// ONE navigation idiom and the modifier-click guard lives in one place.
import { isPlainLeftClick } from 'src/feature/CardOverviewStats/navigationClick'
import StatNavLink from 'src/feature/CardOverviewStats/StatNavLink'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { InfiniteScrollProps } from 'src/types'

import * as style from './CardVirtualCardsTable.style'

/** `DataGrid` hard-codes `columnHeaderHeight={44}`. */
const HEADER_HEIGHT = 44
/**
 * The line-height half of `font_md` (14px/18px). Restated as a literal because
 * `font_md` is a styled-components mixin and this is consumed from MUI's `sx`,
 * which takes plain values.
 */
const CELL_LINE_HEIGHT = '18px'
/**
 * `DataGrid` renders its grid at `height - 60`, an inset sized for the full-page
 * tables it was built for (toolbar + footer). The snapshot has neither — its
 * footer is hidden — so the inset is added back rather than left as dead space
 * under the last row.
 */
const DATA_GRID_HEIGHT_INSET = 60

/**
 * The Pill tone for a card, taken from the design prototype's `vcnStatusType`:
 * live cards read as good, pending as in-flight, and every terminal state stays
 * neutral — a closed or expired card is a normal end, not an error, so none of
 * them use the severe tone.
 *
 * Keyed off the SAME predicates as `psuedoStatusLabel`, which is the text inside
 * the pill, rather than off the raw `VirtualCardStatus`. That label collapses
 * the enum into four buckets — Pending / Active / Closed / Inactive — so reading
 * the raw status here let the two disagree: an `APPROVED` card is neither
 * pending nor active to the label, which renders "Inactive", while a raw-status
 * mapping gave it the in-flight tone. Tone, text and the row treatment below all
 * have to come from one source or rows that read identically look different.
 */
const statusPillColor = (
  virtualCard: VirtualCardListModel,
): 'saas' | 'pending' | undefined => {
  if (virtualCard.isPending) return 'pending'
  if (virtualCard.isActive) return 'saas'
  // Undefined, not a named tone: the Pill's default IS the neutral treatment,
  // and it covers both remaining labels — "Closed" and "Inactive".
  return undefined
}

/**
 * Whether the design dims the row — a card that can no longer be spent.
 *
 * The complement of the two live labels, for the reason above: anything the
 * label calls "Closed" or "Inactive" dims, and nothing it calls "Pending" or
 * "Active" does.
 */
const isTerminal = (virtualCard: VirtualCardListModel): boolean =>
  !virtualCard.isPending && !virtualCard.isActive

type CardVirtualCardsTableRow = {
  id: string
  virtualCard: VirtualCardListModel
}

export type CardVirtualCardsTableProps = {
  className?: string
  /** The virtual cards to render, already filtered and ordered by the caller. */
  virtualCards: VirtualCardListModel[]
  /** True while the first page of virtual cards is in flight. */
  isPending: boolean
  /** True when the virtual-card list request failed. */
  isError?: boolean
  /**
   * The card's total virtual-card count, shown opposite the heading. Omitted on
   * the Overview snapshot, which carries a "view all" link in that slot instead.
   */
  totalCount?: number
  /**
   * The card's ACTIVE virtual-card count. Paired with `totalCount` to render the
   * design's "N active · M total" line — see the prototype's `VirtualCardsTab`.
   * Omitted on the snapshot for the same reason `totalCount` is.
   */
  activeCount?: number
  /**
   * Destination for the heading's "view all" link, built by the mounting page —
   * features do not know URLs. Omitted when there is no Virtual Cards tab to
   * link to, which leaves the heading intact and drops only the link.
   */
  viewAllHref?: string
  /** SPA navigation, supplied by the page. See `StatNavLink`. */
  onNavigate?: (href: string) => void
  /**
   * Builds a row's destination. Supplied by the page — features do not know
   * URLs — so the card-name cell can be a real `<a href>` rather than a click
   * handler.
   *
   * That is what gives rows ⌘-click / middle-click / "open in new tab",
   * right-click → copy link, a hover status-bar preview, and a place in a
   * screen reader's link list. `onSelectVirtualCard` still handles the plain
   * click anywhere else in the row.
   */
  virtualCardHref: (virtualCardId: string) => string
  /** Selecting a row. The page decides what that means (navigate to the card). */
  onSelectVirtualCard: (virtualCardId: string) => void
  /**
   * Starting the create-card flow. Omitted where that flow is unavailable — a
   * user without `ISSUE_VIRTUAL_CARDS` on this card — which drops the CTA
   * rather than offering a dead end. The page owns that decision because the
   * answer is permission-level, not table-level.
   */
  onCreateVirtualCard?: () => void
  /**
   * Paging state for the full Virtual Cards tab. Omitted on the Overview
   * snapshot, which renders a fixed handful of rows and never pages.
   */
  infiniteScrollProps?: InfiniteScrollProps<Record<string, never>>
  /**
   * Explicit grid height for the full Virtual Cards tab, which fills the
   * viewport. Omitted on the snapshot, which sizes itself to its rows.
   */
  height?: number
}

/**
 * The redesigned virtual-card table for the card-detail page.
 *
 * Replaces the legacy `VirtualCardsBox` "Active Cards" grid on the Overview tab.
 * Built as ONE component for both places the design shows the table — the
 * Overview snapshot and, later, the Virtual Cards tab — because they are the
 * same table under two headings, the way `CardBudgetsTable` already is. The
 * differences are all props: the snapshot passes `viewAllHref` and sizes itself
 * to its rows, the full list passes `totalCount` and `infiniteScrollProps`.
 *
 * ⚠️ Only the Overview snapshot is wired up today. The Virtual Cards TAB still
 * renders the legacy grid, and swapping it needs its own feature flag read on
 * `AccountEntityPage` and passed to both call sites — see
 * `isUpdatedBudgetsTableEnabled` for the shape. The Overview snapshot needs no
 * flag of its own because the whole redesigned Overview is already gated.
 *
 * Columns, in design order: Card (card-type glyph + name + last four) ·
 * Cardholder · Type · Status · Spent / limit.
 *
 * The design also shows a Budget column. Dropped on purpose: this table renders
 * on the RCN detail page, where every row already belongs to the card in the
 * heading, so the cell only ever repeats it — on a real account it rendered the
 * RCN's own display name, not a budget. Reconsider when the Virtual Cards TAB
 * adopts this table, where the surrounding context is different.
 *
 * Routing-free by the `src/feature/**` boundary: the page supplies the "view
 * all" destination, the row href, and the selection handler.
 */
export default function CardVirtualCardsTable(
  props: CardVirtualCardsTableProps,
): ReactElement {
  const {
    className,
    virtualCards,
    isPending,
    isError = false,
    totalCount,
    activeCount,
    viewAllHref,
    onNavigate,
    virtualCardHref,
    onSelectVirtualCard,
    onCreateVirtualCard,
    infiniteScrollProps,
    height,
  } = props

  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const users = useSelector(selectUserListModelsFromState)

  const rows = useMemo(
    (): CardVirtualCardsTableRow[] =>
      virtualCards.map((virtualCard) => ({
        id: virtualCard.id,
        virtualCard,
      })),
    [virtualCards],
  )

  /**
   * "$1,100 / $20,000" — whole dollars, matching the design and the sibling
   * budgets table, so the two terminal columns line up as one grammar.
   *
   * `spentCents`/`limitCents` are the model's raw fields; `limit` is its
   * dollars getter. Formatted here rather than via the model's `limitLabel`
   * because that renders `<Crncy>` with cents, which would not match the
   * budgets column beside it.
   *
   * A card with no limit reads "Unlimited" on the right of the slash, the same
   * word `CardBudgetsTable.spentOverLimit` uses for the same state — the two
   * columns sit stacked and are meant to read as one grammar. `limitCents` is
   * typed required, so this is defensive rather than a live bug; without it an
   * omitted limit would put `toDollars(undefined)` through the formatter and
   * render "$NaN" in the cell.
   */
  const spentOverLimit = useCallback(
    (virtualCard: VirtualCardListModel): string => {
      const spent = currencyFormatter({
        amount: virtualCard.spentCents / 100,
        currency: virtualCard.currency,
        maximumFractionDigits: 0,
      })
      const limit =
        virtualCard.limitCents !== undefined
          ? currencyFormatter({
              amount: virtualCard.limit,
              currency: virtualCard.currency,
              maximumFractionDigits: 0,
            })
          : translate('Unlimited')
      return `${spent} / ${limit}`
    },
    [currencyFormatter, translate],
  )

  /**
   * Supplies the header element instead of letting MUI render its own — see
   * `style.columnHeader` for why restyling MUI's is a cascade race. `headerName`
   * stays set alongside it, because that is what MUI reads for the column's
   * accessible name.
   */
  const renderHeader = useCallback(
    (label: string, align?: 'right') => (): ReactElement => (
      <span
        css={align === 'right' ? style.columnHeaderRight : style.columnHeader}
      >
        {label}
      </span>
    ),
    [],
  )

  const columns = useMemo(
    (): Array<GridColDef<CardVirtualCardsTableRow>> => [
      {
        field: 'cardName',
        headerName: translate('Card'),
        renderHeader: renderHeader(translate('Card')),
        flex: 1,
        minWidth: 190,
        sortable: false,
        /*
          Rendered as plain elements rather than through `CardGraphicAndNameCell`
          — and the same goes for the Cardholder column below.

          Those shared cells wrap their content in `DataGridCellWrapper`, which
          adds its own `0 16px` inset. The headers here are pulled to `padding:
          0` (see `sx`) to match the plain-span columns, so a wrapped cell lands
          16px right of the header labelling it — which is exactly the
          misalignment this table shipped with. `CardBudgetsTable` renders plain
          cells for the same reason, and these two tables sit one above the
          other, so they have to inset identically.
        */
        renderCell: (params): ReactElement => {
          const virtualCard = params.row.virtualCard
          return (
            <span css={style.nameCell}>
              {/*
                Decorative: the glyph encodes card type and lifecycle state,
                both of which the Type and Status columns already say in words.
                Alt text would double-announce.
              */}
              <img
                css={style.nameGlyph}
                src={virtualCard.cardTypeImageSrc}
                alt=""
              />
              <span css={style.nameStack}>
                <a
                  css={style.nameLink}
                  href={virtualCardHref(virtualCard.id)}
                  onClick={(event): void => {
                    // Modified clicks and non-primary buttons fall through to
                    // the browser, so open-in-new-tab still works. Shared with
                    // StatNavLink so the two cannot drift.
                    if (!isPlainLeftClick(event)) return
                    event.preventDefault()
                    onSelectVirtualCard(virtualCard.id)
                  }}
                >
                  {virtualCard.truncatedDisplayName}
                </a>
                {/* Masked last four as a subtitle, per the design — not the
                    pill `CardGraphicAndNameCell` renders. */}
                <span css={style.nameSub}>{`••••${virtualCard.last4}`}</span>
              </span>
            </span>
          )
        },
      },
      {
        field: 'cardholder',
        headerName: translate('Cardholder'),
        renderHeader: renderHeader(translate('Cardholder')),
        minWidth: 180,
        flex: 0.8,
        sortable: false,
        // `UserAvatarAndName`, not the grid's `UserAvatarCell` — same reason as
        // the Card column above, and it is what `CardBudgetsTable` uses for its
        // Owner column, so the two avatars line up down the page.
        renderCell: (params): ReactElement | null => {
          const virtualCard = params.row.virtualCard
          const user =
            virtualCard.recipient ?? users[virtualCard.recipientId] ?? undefined
          return user !== undefined ? (
            /*
              Wrapped, and given `textCss`, to make the name ELLIPSE rather than
              wrap. `UserAvatarAndName` sets `overflow: hidden` and
              `text-overflow: ellipsis` but never `white-space: nowrap`, so
              those two do nothing on their own — a long value simply wraps
              instead. An unregistered cardholder is shown by email, and a long
              one wrapped to a second line, pushed "(unregistered)" down, and
              spilled the row's text into the Type column beside it.

              `min-width: 0` on the wrapper is the other half: a flex child
              defaults to `min-width: auto`, which refuses to shrink below its
              content and lets the cell overflow its column no matter what
              overflow rule the text carries.
            */
            <span css={style.cardholderCell}>
              <UserAvatarAndName user={user} textCss={style.cardholderText} />
            </span>
          ) : null
        },
      },
      {
        field: 'cardType',
        headerName: translate('Type'),
        renderHeader: renderHeader(translate('Type')),
        minWidth: 116,
        flex: 0.4,
        sortable: false,
        renderCell: (params): ReactElement => (
          <span css={style.textCell}>
            {params.row.virtualCard.cardTypeLabel}
          </span>
        ),
      },
      {
        field: 'status',
        headerName: translate('Status'),
        renderHeader: renderHeader(translate('Status')),
        minWidth: 100,
        flex: 0.35,
        sortable: false,
        // A Pill, per the design, not the plain text this shipped with. The
        // three tones are the prototype's: Active reads as good, Pending as
        // in-flight, and every terminal state is neutral rather than alarming —
        // a closed card is not an error.
        renderCell: (params): ReactElement => (
          <Pill size="small" color={statusPillColor(params.row.virtualCard)}>
            {params.row.virtualCard.psuedoStatusLabel}
          </Pill>
        ),
      },
      {
        field: 'spentLimit',
        headerName: translate('Spent / limit'),
        renderHeader: renderHeader(translate('Spent / limit'), 'right'),
        minWidth: 150,
        flex: 0.6,
        sortable: false,
        // Terminal column, right-aligned per the design. `align`/`headerAlign`
        // rather than CSS so MUI keeps the cell and its header in step.
        align: 'right',
        headerAlign: 'right',
        renderCell: (params): ReactElement => (
          <span css={style.amountCell}>
            {spentOverLimit(params.row.virtualCard)}
          </span>
        ),
      },
    ],
    [
      translate,
      users,
      spentOverLimit,
      renderHeader,
      virtualCardHref,
      onSelectVirtualCard,
    ],
  )

  /**
   * The full Virtual Cards tab, as opposed to the Overview snapshot. Paging is
   * the honest discriminator: only the full list supplies it, and it is exactly
   * the property that decides whether the grid must virtualize and size itself
   * against a scrollbar.
   */
  const isPaged = infiniteScrollProps !== undefined

  const isEmpty = !isPending && !isError && rows.length === 0

  /**
   * Exactly the height the loaded rows occupy.
   *
   * `DATA_GRID_UNMEASURED_BORDER_HEIGHT` is not slack — see its definition. Two
   * borders sit outside MUI's height model (this table's header hairline being
   * one of them), so without it the rows overflow the viewport by 2px and MUI
   * paints a vertical scrollbar down a snapshot that shows at most three rows.
   */
  const contentHeight =
    HEADER_HEIGHT +
    rows.length * DataGridRowHeight.Default +
    DATA_GRID_HEIGHT_INSET +
    DATA_GRID_UNMEASURED_BORDER_HEIGHT

  /**
   * The snapshot always sizes to its rows. The full tab asks for a
   * viewport-filling height, which is right while the list can still grow and
   * wrong once it cannot — a card with three virtual cards got a table with a
   * screen's worth of blank space under the last row.
   *
   * Clamped ONLY once the list is complete, the same rule and the same reason
   * as `CardBudgetsTable`: shrinking to content while more pages exist would
   * stop the scroller from overflowing, and the scroll that never happens is
   * the one that fetches page two — the list would wedge on whatever it had
   * loaded.
   */
  const listComplete =
    infiniteScrollProps !== undefined &&
    infiniteScrollProps.complete &&
    infiniteScrollProps.hasNextPage !== true

  const gridHeight =
    height === undefined
      ? contentHeight
      : listComplete
        ? Math.min(height, contentHeight)
        : height

  const body = (): ReactElement => {
    if (isPending) {
      return (
        <p css={style.statusBody} role="status">
          {translate('Loading virtual cards')}
        </p>
      )
    }

    if (isError) {
      return (
        <p css={style.statusBody} role="alert">
          {translate("We couldn't load virtual cards. Try again later.")}
        </p>
      )
    }

    if (isEmpty) {
      return (
        <p css={style.statusBody}>
          {translate('No virtual cards on this card yet.')}
        </p>
      )
    }

    return (
      <DataGrid<Record<string, never>, CardVirtualCardsTableRow>
        css={style.grid}
        rows={rows}
        columns={columns}
        filtersBody={{}}
        rowHeight={DataGridRowHeight.Default}
        // Terminal cards dim, per the design — they stay readable but stop
        // competing with the live ones.
        getRowClassName={(params): string =>
          isTerminal(params.row.virtualCard) ? 'dimmed' : ''
        }
        // The design's header is a plain kicker row over a hairline, not the
        // shared grid's filled band.
        headerStyle={DataGridHeaderStyle.NoBackground}
        height={gridHeight}
        infiniteScrollProps={infiniteScrollProps}
        // Fixed column set, and all of it has to be said explicitly —
        // DataGridPro ships drag-reorder and resize on by default.
        // `disableColumnMenu` subsumes hiding and is the only one that removes
        // the ⋮ entirely, which matters because every column here is
        // `sortable: false` and a sort-only menu would open empty.
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        pinFirstColumn={false}
        checkboxSelection={false}
        // No pagination and no selection, so MUI's default footer contributes a
        // row total and ~52px of chrome that says nothing the heading doesn't.
        hideFooter
        // Only for the fixed-height snapshot, whose handful of rows never need
        // windowing and where virtualization is what makes the scroller's
        // measured height wobble as it settles — which is what makes page
        // scroll catch on the table. The paged tab keeps it ON.
        disableVirtualization={!isPaged}
        // ⚠️ `DataGrid` merges consumer `sx` with a SHALLOW spread, so any key
        // here REPLACES its rule rather than extending it. `.MuiDataGrid-cell`
        // is the one key below that the shared grid also sets, so its three
        // declarations are restated verbatim — dropping them collapses every
        // cell's flex centring and re-adds padding the row heights assume.
        //
        // These live in `sx` rather than the `css` prop because MUI injects its
        // own rules through emotion on the grid root; a styled-components
        // descendant selector is a specificity race against them, and `sx` is
        // the channel that reliably wins.
        sx={{
          // No resizing and no reordering here, so the grab handles the shared
          // grid keeps at `opacity: 1` have nothing to grab — they only read as
          // column dividers, which the design does not have.
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          '& .MuiDataGrid-columnHeader': {
            padding: 0,
            // ↓ restated from the shared grid — see the shallow-merge note.
            // The background matters: on the paged list the header is sticky
            // over scrolling rows, and a transparent one lets them show through.
            backgroundColor: base_colorRaisedSurface,
            borderLeftStyle: 'unset',
          },
          // One hairline under the whole header row; the shared grid marks the
          // band with a background fill instead, which NoBackground removes.
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: `1px solid ${base_colorContentBorder}`,
          },
          // The scrollbar gutter's filler cell carries a left border, which
          // reads as a stray rule floating past the last column.
          '& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-filler': {
            borderLeft: 'none',
          },
          // The snapshot is sized to exactly fit its rows, so it is never meant
          // to scroll — but the header's hairline and sub-pixel row heights
          // leave a pixel or two of scrollable range, which paints a scrollbar
          // and makes the scroller swallow wheel events the page should get.
          // Same fix, same reason, as the two sibling Overview panels.
          ...(isPaged
            ? {}
            : {
                /*
                  The vertical scrollbar itself, belt-and-braces over the height
                  arithmetic above.

                  In MUI X v8 this is a separate absolutely-positioned overlay
                  element, not the virtual scroller — which is why the
                  `overflowY: 'hidden'` below never suppressed it. The snapshot
                  is sized to exactly fit its rows and can never have anywhere
                  to scroll, so any range MUI finds is chrome it mismeasured,
                  and the honest response is to not draw the control.

                  The PAGED list genuinely scrolls, so it must never get this.
                */
                '& .MuiDataGrid-scrollbar--vertical': { display: 'none' },
                '& .MuiDataGrid-virtualScroller': {
                  // ↓ restated from the shared grid — see the shallow-merge
                  // note. Losing `overscrollBehaviorX` in particular would let
                  // a horizontal trackpad swipe trigger browser BACK.
                  willChange: 'transform',
                  transform: 'translateZ(0)',
                  contain: 'layout paint',
                  backfaceVisibility: 'hidden',
                  overscrollBehaviorX: 'none',
                  overscrollBehaviorY: 'auto',
                  overflowY: 'hidden',
                },
              }),
          '& .MuiDataGrid-cell': {
            // ↓ restated from the shared grid — see the shallow-merge note.
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            // Body cells sit at 14px regular; the default inherits a larger,
            // heavier face from the page.
            fontSize: font_size_md,
            fontFamily: font_family_weight_regular,
            color: text_colorTextPrimary,
            // `line-height` is NOT optional here, and it is the one the shared
            // grid never had to set. Its own `DataGridCell` applies `font_md`
            // (14px/18px) to the text it renders; these cells are plain
            // elements, so without this they inherit the page's much taller
            // line box. That is what dropped the name in `UserAvatarAndName`
            // below its avatar — the component row-aligns on `flex-start`, so
            // a tall line box centres the glyphs well under the picture.
            lineHeight: CELL_LINE_HEIGHT,
          },
        }}
        onRowClick={(params, event): void => {
          /*
            The same guard the name-cell anchor uses, and it has to be here too.
            A ⌘-click on that anchor deliberately does NOT preventDefault — it
            lets the browser open a new tab — but the event still bubbles to
            this handler. Checking only `defaultPrevented` would navigate the
            CURRENT tab as well, so the user gets both.
          */
          if (!isPlainLeftClick(event)) return
          onSelectVirtualCard(params.row.virtualCard.id)
        }}
        // Row navigation must not be mouse-only (src/CLAUDE.md WCAG AA floor).
        // MUI gives cells a roving tabindex, so Enter on the focused cell is the
        // keyboard equivalent of clicking its row.
        onCellKeyDown={(params, event): void => {
          if (event.key !== 'Enter' || event.defaultPrevented) return
          event.defaultMuiPrevented = true
          onSelectVirtualCard(params.row.virtualCard.id)
        }}
      />
    )
  }

  return (
    <ContentCard
      className={className}
      css={style.panel}
      data-testid="cardVirtualCardsTable"
    >
      <div css={style.header}>
        <h2 css={style.title}>{translate('Virtual Cards')}</h2>
        <div css={style.headerActions}>
          {viewAllHref !== undefined && (
            <StatNavLink href={viewAllHref} onNavigate={onNavigate}>
              {/* Arrow lives in the source string, matching the sibling
                  Overview links ("View budgets →", "Adjust limit →"). */}
              {translate('View all virtual cards →')}
            </StatNavLink>
          )}
          {viewAllHref === undefined && totalCount !== undefined && (
            <span css={style.count}>
              {/* The design's count line. `activeCount` lands with the same
                  statistics payload as the total, so the two arrive together
                  and the line never renders half-populated. */}
              {activeCount !== undefined
                ? translate('${active} active · ${total} total', {
                    active: `${activeCount}`,
                    total: `${totalCount}`,
                  })
                : translate('${total} total', { total: `${totalCount}` })}
            </span>
          )}
          {/*
            The design's New virtual card CTA. Rendered only when the page
            supplies a handler, which is how a user without ISSUE_VIRTUAL_CARDS
            on this account gets the heading without a button that would
            dead-end them.

            It is also what keeps the empty state from being a dead end: the
            legacy `VirtualCardsBox` carried its create affordance inside an
            illustrated `TableEmptyState`, and this table's empty state is a
            plain line of text instead.
          */}
          {onCreateVirtualCard !== undefined && (
            <Button
              variant="secondary"
              onClick={onCreateVirtualCard}
              data-testid="cardVirtualCardsTable-createVirtualCard"
            >
              {translate('New virtual card')}
            </Button>
          )}
        </div>
      </div>
      {body()}
    </ContentCard>
  )
}
